import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import { Button } from '@sentinel/ui';
import type { ConnectedApp } from '../types';
import { ScopeList } from './scope-list';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';

const styles = stylex.create({
  row: {
    display: 'grid',
    gap: '0.5rem',
    padding: '1rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
    borderRadius: '0.5rem',
    backgroundColor: `color-mix(in oklab, ${sentinel.surfaceElevated} 60%, transparent)`,
  },
  header: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  name: { margin: 0, fontSize: '1rem', fontWeight: 600 },
  meta: {
    margin: 0,
    fontSize: '0.8125rem',
    color: sentinel.textMuted,
  },
  scopesLabel: { margin: 0, fontSize: '0.875rem', fontWeight: 500 },
});

// The only NaN-guarded date formatter in the repo
// (apps/admin/src/features/idps/components/domain-hints-panel.tsx). No
// shared helper exists anywhere in web/ for this.
function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

export interface ConnectedAppRowProps {
  app: ConnectedApp;
  revoking: boolean;
  onRevoke: (app: ConnectedApp) => void;
}

export function ConnectedAppRow({ app, revoking, onRevoke }: ConnectedAppRowProps) {
  const { t } = useTranslation('connected-apps');

  return (
    <li {...stylex.props(styles.row)} data-testid={`connected-app-row-${app.id}`}>
      <div {...stylex.props(styles.header)}>
        <div>
          <h2 {...stylex.props(styles.name)} data-testid={`connected-app-name-${app.id}`}>
            {app.client_name ?? app.client_id}
          </h2>
          <p {...stylex.props(styles.meta)}>
            {t('app_id_label')}: <code>{app.client_id}</code>
          </p>
        </div>
        <Button
          type="button"
          variant="danger"
          size="sm"
          disabled={revoking}
          data-testid={`connected-app-revoke-${app.id}`}
          onClick={() => onRevoke(app)}
        >
          {t('revoke')}
        </Button>
      </div>
      <p {...stylex.props(styles.meta)}>{t('granted_on', { date: formatDate(app.granted_at) })}</p>
      {app.expires_at ? (
        <p {...stylex.props(styles.meta)}>
          {t('expires_on', { date: formatDate(app.expires_at) })}
        </p>
      ) : null}
      <p {...stylex.props(styles.scopesLabel)} id={`connected-app-scopes-label-${app.id}`}>
        {t('scopes_title')}
      </p>
      <ScopeList
        scopes={app.scopes}
        testId={`connected-app-scopes-${app.id}`}
        labelledBy={`connected-app-scopes-label-${app.id}`}
      />
    </li>
  );
}
